import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/MainContainer.css';
import './ParticipantGroupMemberList.css';
import api from '../wrapper/API';
import CustomDropdown from '../input/CustomDropdown';
import ListItem from '../list/ListItem';
import ListTextItem from '../list/ListTextItem';
import ListSymbols from '../list/ListSymbols';
import ListSymbol from '../list/ListSymbol';
import Icon from '../home/Icon';
import ContextMenu from '../context-menu/ContextMenu';
import ContextMenuItem from '../context-menu/ContextMenuItem';
import { useContextMenu } from '../context/ContextMenuContext';
import Popup from '../popup/Popup';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import ParticipantGroupDot from '../participant-group/ParticipantGroupDot';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import { ROUTES } from '../../util/routes';
import apiErrorText from '../player/apiErrorText';
import { endOfYear, formatDate, isoDate, rangeLabel, rangesOverlap, todayIso, yearOf } from '../player/membershipDates';

const CONTEXT_MENU = 'participantgroupmember';

/**
 * Tab "Gruppen" on /users (UX decision 7): group + year selection, the team (Orga/Admin role periods of
 * the year, GET /Role/GetTeam) fixed on top and below the members of the selected group
 * (GET /ParticipantGroupMembership/GetByGroup). The API already hides role-driven rows while a role covers
 * the year; rows of team members that overlap their role period are not repeated here either.
 * Responses are cached per year / group+year, so switching back and forth costs no requests.
 * Calendar-year groups offer "Um ein Jahr verlängern" in the context menu (Admin).
 */
function ParticipantGroupMemberList() {
  const navigate = useNavigate();
  const { groups: allGroups, loaded, failed } = useParticipantGroups();
  const { contextMenuData } = useContextMenu();
  const { openPopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();

  const today = todayIso();
  const currentYear = yearOf(today);
  // Archived groups follow the active ones: they keep their past members (the group editor points here).
  const groups = useMemo(() => {
    const others = allGroups.filter(g => !g.isGuest);
    return [...others.filter(g => !g.isArchived), ...others.filter(g => g.isArchived)];
  }, [allGroups]);

  const [groupId, setGroupId] = useState(null);
  const [year, setYear] = useState(currentYear);
  const [yearsByGroup, setYearsByGroup] = useState({});   // groupId → [years]
  const [teamByYear, setTeamByYear] = useState({});       // year → { list, failed }
  const [membersByKey, setMembersByKey] = useState({});   // `${groupId}:${year}` → { list, failed }
  const requested = useRef(new Set());

  const [pendingExtend, setPendingExtend] = useState(null);
  const [error, setError] = useState('');

  // Default group: the first selectable group (active ones first).
  useEffect(() => {
    if (groupId != null && groups.some(g => g.id === groupId)) return;
    const fallback = groups[0] ?? null;
    setGroupId(fallback?.id ?? null);
  }, [groups, groupId]);

  // One request per cache key; a failed one may be retried by selecting it again.
  const load = useCallback((cacheKey, url, store) => {
    if (requested.current.has(cacheKey)) return;
    requested.current.add(cacheKey);
    api.get(url)
      .then(res => store({ list: Array.isArray(res.data) ? res.data : [], failed: false }))
      .catch(() => {
        requested.current.delete(cacheKey);
        store({ list: [], failed: true });
      });
  }, []);

  useEffect(() => {
    if (groupId == null) return;
    load(`years:${groupId}`, `/ParticipantGroupMembership/GetAvailableYears?participantGroupId=${groupId}`,
      ({ list }) => setYearsByGroup(prev => ({ ...prev, [groupId]: list })));
  }, [groupId, load]);

  useEffect(() => {
    load(`team:${year}`, `/Role/GetTeam?year=${year}`,
      (entry) => setTeamByYear(prev => ({ ...prev, [year]: entry })));
  }, [year, load]);

  useEffect(() => {
    if (groupId == null) return;
    const key = `${groupId}:${year}`;
    load(`members:${key}`, `/ParticipantGroupMembership/GetByGroup?participantGroupId=${groupId}&year=${year}`,
      (entry) => setMembersByKey(prev => ({ ...prev, [key]: entry })));
  }, [groupId, year, load]);

  const group = groups.find(g => g.id === groupId) ?? null;
  // Archived groups keep only past memberships — the API refuses extending them.
  const canExtend = group?.validityMode === 'CalendarYear' && !group.isArchived;

  // Selected year stays selectable even if the newly selected group has no data for it.
  const years = useMemo(() => {
    const set = new Set([...(yearsByGroup[groupId] ?? []), currentYear, year]);
    return [...set].sort((a, b) => b - a);
  }, [yearsByGroup, groupId, currentYear, year]);

  const teamEntry = teamByYear[year];
  // Members wait for the team as well, so team members never flash up in the member list.
  const membersEntry = groupId != null && teamEntry ? membersByKey[`${groupId}:${year}`] : undefined;
  const team = useMemo(() => teamEntry?.list ?? [], [teamEntry]);

  const members = useMemo(() => {
    const rolesByPlayer = new Map();
    team.forEach(r => rolesByPlayer.set(r.playerId, [...(rolesByPlayer.get(r.playerId) ?? []), r]));
    return (membersEntry?.list ?? []).filter(m => !(rolesByPlayer.get(m.playerId) ?? [])
      .some(r => rangesOverlap(r.validFrom, r.validTo, m.validFrom, m.validTo)));
  }, [membersEntry, team]);

  const handleRowClick = (playerId) => navigate(ROUTES.user(playerId));

  const askExtend = () => {
    const target = members.find(m => m.id === contextMenuData.identifier);
    if (!target) return;
    setPendingExtend(target);
    openPopup('participantgroupmemberextend', target.id);
  };

  const handleConfirmExtend = () => {
    const target = pendingExtend;
    if (!target) return;
    startSuccessBarLoading('Mitgliedschaft wird verlängert');
    api.post(`/ParticipantGroupMembership/Extend/${target.id}`)
      .then(res => {
        startSuccessBarSuccess('Um ein Jahr verlängert');
        const updated = res.data ?? { ...target, validTo: endOfYear(yearOf(target.validTo) + 1) };
        const groupKey = `${updated.participantGroupId}:`;
        // Patch the visible list; other cached years of this group may now be incomplete → reload on demand.
        setMembersByKey(prev => {
          const next = {};
          Object.entries(prev).forEach(([key, entry]) => {
            if (key === `${groupId}:${year}`) {
              next[key] = { ...entry, list: entry.list.map(m => (m.id === updated.id ? updated : m)) };
            } else if (!key.startsWith(groupKey)) {
              next[key] = entry;
            } else {
              requested.current.delete(`members:${key}`);
            }
          });
          return next;
        });
        // The extension may add a year to the selection (the API lists at most next year).
        const newYear = yearOf(updated.validTo);
        if (newYear <= currentYear + 1) {
          setYearsByGroup(prev => {
            const list = prev[updated.participantGroupId] ?? [];
            return list.includes(newYear) ? prev : { ...prev, [updated.participantGroupId]: [...list, newYear] };
          });
        }
      })
      .catch(err => {
        closeSuccessBar();
        setError(apiErrorText(err, 'Verlängern nicht möglich.'));
        openPopup('participantgroupmembererror', target.id);
      });
  };

  const renderRow = (row, index, { periodText, dot, contextMenu }) => (
    <ListItem
      key={`${contextMenu ? 'm' : 't'}${row.id}`}
      identifier={row.id}
      gridTemplateColumns={'auto 1fr auto'}
      contextMenuIdentifier={contextMenu ? CONTEXT_MENU : ''}
      index={Math.min(index, 20)}
      onClick={() => handleRowClick(row.playerId)}
      className="userMemberCard"
    >
      {dot}
      <ListTextItem type="primary">
        <div className="userNameCell">
          <span className="userNameLast">{row.lastName}</span>
          {row.firstName && <span className="userNameFirst">{row.firstName}</span>}
        </div>
      </ListTextItem>
      <ListSymbols>
        <ListSymbol icon={"visibility_off"} visible={!!row.isAnonymized} type={1} tooltip={'Anonymisiert'} />
      </ListSymbols>
      <ListTextItem type="meta">
        <div className="userContactRow">
          {row.email && (
            <React.Fragment>
              <Icon icon="mail" className="userContactIcon" />
              <span>{row.email}</span>
            </React.Fragment>
          )}
        </div>
      </ListTextItem>
      <ListTextItem type="meta">
        <span className="userPeriodCell">{periodText}</span>
      </ListTextItem>
    </ListItem>
  );

  if (!loaded) {
    return <div className={'memberListEmpty'}>{failed ? 'Gruppen konnten nicht geladen werden.' : 'Lädt …'}</div>;
  }
  if (groups.length === 0) {
    return <div className={'memberListEmpty'}>Es gibt keine Gruppen mit Mitgliedschaften.</div>;
  }

  const pluralName = group?.pluralName || group?.name || '';

  return (
    <React.Fragment>
      <div className={'memberFilterBar'}>
        <div className={'memberFilterDropdown'}>
          <CustomDropdown icon="groups" value={String(groupId ?? '')} onChange={(e) => setGroupId(Number(e.target.value))}>
            {groups.map(g => (
              <option key={g.id} value={g.id}>{`${g.pluralName || g.name}${g.isArchived ? ' (archiviert)' : ''}`}</option>
            ))}
          </CustomDropdown>
        </div>
        <div className={'memberFilterDropdown'}>
          <CustomDropdown icon="calendar_month" value={String(year)} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </CustomDropdown>
        </div>
      </div>

      <div className={'memberList'}>
        {team.length > 0 && (
          <React.Fragment>
            <div className={'memberSectionLabel'}>
              <span className={'memberSectionLabelText'}>Team</span>
              <span className={'memberSectionLabelHint'}>Orga &amp; Admin</span>
              <span className={'memberSectionCount'}>{team.length}</span>
            </div>
            {team.map((r, index) => renderRow(r, index, {
              periodText: r.isActive ? `seit ${formatDate(r.validFrom)}` : rangeLabel(r.validFrom, r.validTo, today),
              dot: <ParticipantGroupDot role={r.role} />,
              contextMenu: false,
            }))}
          </React.Fragment>
        )}

        <div className={'memberSectionLabel'}>
          <span className={'memberSectionLabelText'}>{pluralName}</span>
          <span className={'memberSectionLabelHint'}>{year}</span>
          <span className={'memberSectionCount'}>{membersEntry ? members.length : ''}</span>
        </div>
        {!membersEntry && <div className={'memberListEmpty'}>Lädt …</div>}
        {membersEntry?.failed && <div className={'memberListEmpty'}>{pluralName} konnten nicht geladen werden.</div>}
        {membersEntry && !membersEntry.failed && members.length === 0 && (
          <div className={'memberListEmpty'}>Keine {pluralName} in {year}.</div>
        )}
        {members.map((m, index) => renderRow(m, index, {
          periodText: m.validTo ? `${formatDate(m.validFrom)} – ${formatDate(m.validTo)}` : `${rangeLabel(m.validFrom, null, today)} · unbefristet`,
          dot: <ParticipantGroupDot participantGroup={group} />,
          contextMenu: canExtend && !!isoDate(m.validTo),
        }))}
        {canExtend && members.length > 0 && (
          <p className={'memberListHint'}>Verlängern um ein Jahr: Rechtsklick bzw. langes Drücken auf eine Zeile.</p>
        )}
      </div>

      <ContextMenu type={CONTEXT_MENU}>
        <ContextMenuItem label={'Um ein Jahr verlängern'} icon={'add'} type={2} onClick={askExtend} />
      </ContextMenu>
      <Popup name='participantgroupmemberextend' confirmText='+1 Jahr' headerText='Mitgliedschaft verlängern'
        type="info" onConfirm={handleConfirmExtend} withCancel
      >
        <p>
          <strong>{`${pendingExtend?.firstName ?? ''} ${pendingExtend?.lastName ?? ''}`.trim()}</strong>
          {pendingExtend?.validTo
            ? `: Das Bis-Datum wird um ein Jahr verschoben – ${formatDate(pendingExtend.validTo)} → ${formatDate(endOfYear(yearOf(pendingExtend.validTo) + 1))}.`
            : ''}
        </p>
        <p className={'formFieldHint'}>Zukünftige Spieltage des Spielers werden neu berechnet. Es wird keine Willkommensmail versendet.</p>
      </Popup>
      <Popup name='participantgroupmembererror' confirmText='Ok' headerText='Fehler' type="error">
        <p>{error}</p>
      </Popup>
    </React.Fragment>
  );
}

export default ParticipantGroupMemberList;
